import {
    Contract,
    ContractDetails as TContractDetails,
    hasUnpaidAmmount,
    isVDLContract,
    isBalloonLeasing,
    isCredit,
    isFinancialLease,
    isLongTermOrOperatingLeasing,
} from 'cpfr-common';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import {
    ContractActions,
    Skeleton,
    useAnalyticsActionTracker,
    ContractActionItemType,
} from '@cp-shared-19/frontend-ui';
import { useMyProfile } from 'components/my-profile';
import { earlyReturnPenaltyPagePath, earlySettlementPagePath } from 'components/navigation/paths';
import moment, { Moment } from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { getFirstEligibleDateForEarlyReturnPenalty } from 'utils/early-return-penalty-utils';
import { withLoadingAndNoConnectionHandler } from '../../integration-wrapper';
import { ContractDetailsUi } from './ui/ContractDetailsUi';

export const ContractDetails: React.FC<{
    contract: Contract & TContractDetails;
    isContactInformationChangePending: boolean;
    setShowSuccessNotification: (val: boolean) => void;
}> = ({ contract, isContactInformationChangePending, setShowSuccessNotification }) => {
    const { data: myProfileData, isLoading: isLoadingProfile, loadingError: profileLoadingError } = useMyProfile();
    const { t } = useTranslation('contracts');
    const history = useHistory();
    const { onAction } = useAnalyticsActionTracker('onOpenSuspendInstallment');
    const ContractDetailsWithHandlers = useMemo(() => withLoadingAndNoConnectionHandler(ContractDetailsUi), []);
    const actionItems: ContractActionItemType[] = [];

    const isSMECustomer = () => {
        return myProfileData?.identification?.customerType
            ? myProfileData?.identification?.customerType !== 'Retail'
            : false;
    };

    const isCreditContract = isCredit(contract.productType);
    const isBalloonLeasingType = isBalloonLeasing(contract.productType);
    const isFinancialLeasing = isFinancialLease(contract.productType);
    const isOperativeLeasing = isLongTermOrOperatingLeasing(contract.productType);
    const isLeasingSocial = contract.isLeasingSocial;
    const lastsLongerThan12Months = formatCpDate(contract.contractStartDate)
        .add(12, 'month')
        .toMoment()
        .isBefore(new Date());
    const firstDateForEarlyReturnPenalty: Moment | undefined = getFirstEligibleDateForEarlyReturnPenalty(contract);
    const isReturnPenaltyDisabled =
        formatCpDate(contract.contractEndDate).toMoment().month() === moment().month() ||
        formatCpDate(contract.contractEndDate).toMoment().isBefore(new Date()) ||
        !firstDateForEarlyReturnPenalty ||
        isVDLContract(contract) ||
        hasUnpaidAmmount(contract);

    if (
        contract.isActive &&
        (isCreditContract ||
            (isFinancialLeasing && lastsLongerThan12Months) ||
            (isBalloonLeasingType && lastsLongerThan12Months))
    ) {
        actionItems.push({
            iconName: 'semantic-calculator',
            pageUrl: earlySettlementPagePath(contract.contractId),
            label: contract?.productTypeFR?.toLowerCase()?.includes('crédit bail')
                ? t('contract-actions.financial-lease-early-settlement')
                : t('contract-actions.early-settlement'),
            onClick: () => onAction(),
            title: contract?.productTypeFR?.toLowerCase()?.includes('crédit bail')
                ? t('contract-actions.early-aria-title-btn2')
                : t('contract-actions.early-settlement'),
            ariaLabel: contract?.productTypeFR?.toLowerCase()?.includes('crédit bail')
                ? t('contract-actions.early-aria-title-btn2')
                : t('contract-actions.early-settlement'),
        });
    }

    if (contract.isActive && isOperativeLeasing && lastsLongerThan12Months && !isLeasingSocial) {
        actionItems.push({
            iconName: 'semantic-calculator',
            label: t('contract-actions.early-return-penalty'),
            onClick: () => {
                history.push(earlyReturnPenaltyPagePath(contract.contractId));
                onAction();
            },
            disabled: isReturnPenaltyDisabled,
            title: t('contract-actions.early-aria-title-btn'),
            ariaLabel: t('contract-actions.early-aria-title-btn'),
        });
    }

    return (
        <>
            <div className="u-pb-small u-mt-xxsmall">
                {!isContactInformationChangePending && <ContractActions contractActionItems={actionItems} />}
            </div>
            <ContractDetailsWithHandlers
                isLoading={isLoadingProfile}
                contract={contract}
                isSMECustomer={isSMECustomer()}
                hasError={!!profileLoadingError}
                isContactInformationChangePending={isContactInformationChangePending}
                loadingPlaceholder={<Skeleton height={30} count={4} className="u-mt-small" />}
                myProfileData={myProfileData}
                setShowSuccessNotification={setShowSuccessNotification}
            />
        </>
    );
};
